import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Gauge, RefreshCw, Info, Pencil, RotateCcw, Check,
  CalendarRange, AlertTriangle, Sparkles, MapPin, Users,
  RefreshCcwDot, CloudOff, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtEur, fmtInt } from '@/lib/format';

const eur0 = (v) => fmtEur(v || 0, 0);
const eur2 = (v) => fmtEur(v || 0, 2);
const pct = (r) => `${Math.round((r || 0) * 100)}%`;

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const moisLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MOIS_FR[parseInt(m, 10) - 1]} ${y}`;
};

const SOURCE_CFG = {
  forecast: { label: 'Forecast', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  reel: { label: 'Réel à date', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300' },
  estime: { label: 'Estimé', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  estime_tr: { label: 'Estimé (TR)', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  corrige: { label: 'Corrigé', cls: 'text-white' },
};

const KIND_STYLE = {
  revenu: 'font-semibold',
  soustotal: 'bg-muted/40 font-medium',
  total: 'bg-muted/50 font-semibold',
  resultat: 'border-t-2 font-bold',
};

export const ForecastView = ({ onChanged, months }) => {
  // `months` = liste des mois forecast affichés dans le tableau (ex. ['2026-09','2026-10']).
  // Par défaut on inspecte le DERNIER (= mois en cours), mais on peut basculer sur un
  // mois intermédiaire comblé (ex. septembre non finalisé) pour le corriger lui aussi.
  const monthList = (months || []).slice().sort();
  const [data, setData] = useState(null);
  const [mois, setMois] = useState(monthList.length ? monthList[monthList.length - 1] : null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingQonto, setSyncingQonto] = useState(false);
  const [detailRow, setDetailRow] = useState(null);     // row for detail dialog
  const [ovrRow, setOvrRow] = useState(null);           // row key for override popover
  const [ovrVal, setOvrVal] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: d } = await api.get('/rex/forecast/month', { params: mois ? { mois } : {} });
      setData(d);
    } catch (e) { toast.error("Chargement du forecast impossible"); }
    if (!silent) setLoading(false);
  }, [mois]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load(true);
    setRefreshing(false);
    toast.success('Forecast actualisé');
  };

  const syncQonto = async () => {
    setSyncingQonto(true);
    try {
      const { data: d } = await api.post('/rex/forecast/qonto/sync', null, { params: { mois: data.mois } });
      if (d.forecast) setData(d.forecast);
      const q = d.qonto || {};
      if (!q.configured) {
        toast.error('Qonto non connecté — déposez la clé API (lecture seule) dans Suivi Social → Qonto.');
      } else if (q.error) {
        toast.error(`Synchronisation Qonto échouée (${q.error}).`);
      } else {
        toast.success(`Qonto synchronisé — ${q.nb_txns || 0} transaction(s) réelle(s) ce mois.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Synchronisation Qonto impossible'); }
    setSyncingQonto(false);
    onChanged && onChanged();
  };

  const submitOverride = async (key) => {
    const raw = String(ovrVal).replace(',', '.').trim();
    if (raw === '' || isNaN(Number(raw))) { toast.error('Montant invalide'); return; }
    try {
      const { data: d } = await api.patch('/rex/forecast/override', {
        mois: data.mois, key, value: Number(raw),
      });
      setData(d.forecast);
      toast.success('Poste corrigé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Correction impossible'); }
    setOvrRow(null); setOvrVal('');
    onChanged && onChanged();
  };

  const resetOverride = async (key) => {
    try {
      const { data: d } = await api.post('/rex/forecast/reset', { mois: data.mois, key });
      setData(d.forecast);
      toast.success('Poste réinitialisé (calcul auto)');
    } catch (e) { toast.error('Réinitialisation impossible'); }
    setOvrRow(null); setOvrVal('');
    onChanged && onChanged();
  };

  const setRate = async (key, pctValue) => {
    const rate = Math.max(0, Math.min(100, Number(pctValue) || 0)) / 100;
    try {
      const { data: d } = await api.patch('/rex/forecast/override', {
        mois: data.mois, key, value: rate,
      });
      setData(d.forecast);
      setDetailRow((r) => (r && r.key === 'ca' ? { ...r } : r));
      toast.success('Taux mis à jour');
    } catch (e) { toast.error('Mise à jour impossible'); }
    onChanged && onChanged();
  };

  const resetRate = async (key) => {
    try {
      const { data: d } = await api.post('/rex/forecast/reset', { mois: data.mois, key });
      setData(d.forecast);
      toast.success('Taux réinitialisé (auto)');
    } catch (e) { toast.error('Réinitialisation impossible'); }
    onChanged && onChanged();
  };

  const addRemboursement = async (payload) => {
    try {
      const { data: d } = await api.post('/rex/forecast/remboursements', { mois: data.mois, ...payload });
      setData(d.forecast);
      toast.success('Remboursement ajouté');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ajout impossible'); }
    onChanged && onChanged();
  };

  const delRemboursement = async (rid) => {
    try {
      const { data: d } = await api.delete(`/rex/forecast/remboursements/${rid}`, { params: { mois: data.mois } });
      setData(d.forecast);
      toast.success('Remboursement supprimé');
    } catch (e) { toast.error('Suppression impossible'); }
    onChanged && onChanged();
  };

  const setRegion = async (matricule, region) => {
    try {
      const { data: d } = await api.post('/rex/forecast/region', { mois: data.mois, matricule, region });
      if (d.forecast) setData(d.forecast);
      toast.success(region === 'Indéterminée' ? 'Affectation levée (retour auto)' : `Région affectée : ${region}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Affectation impossible'); }
    onChanged && onChanged();
  };

  if (loading) {
    return <div className="space-y-3" data-testid="rex-forecast-loading"><Skeleton className="h-24 w-full" /><Skeleton className="h-80 w-full" /></div>;
  }
  if (!data) return null;

  const rex = data.rows.find((r) => r.key === 'rex');
  const ca = data.rows.find((r) => r.key === 'ca');

  return (
    <div className="space-y-4" data-testid="rex-forecast-view">
      {/* Bandeau explicatif */}
      <div className="flex flex-wrap items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm dark:border-sky-900 dark:bg-sky-950/40">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
        <div className="flex-1 text-sky-900 dark:text-sky-200">
          <b>Mode temps réel.</b> Les mois précédents restent le <b>réel figé</b> (onglet « Tableau »).
          Ce tableau estime le <b>Résultat d'Exploitation à date</b> du mois en cours, recalculable chaque jour,
          <b> sans jamais modifier le réel</b>. Chaque poste estimé est corrigeable et réinitialisable.
        </div>
      </div>

      {/* En-tête / KPIs */}
      <Card className="card-shadow">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: 'rgba(238,114,46,0.12)' }}>
              <Gauge className="h-5 w-5" style={{ color: 'var(--tenant-accent-600)' }} />
            </span>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Résultat d'exploitation à date</div>
              <div className="text-2xl font-bold tabular-nums" style={{ color: rex && rex.value >= 0 ? '#059669' : '#dc2626' }} data-testid="rex-forecast-rex">
                {eur0(rex ? rex.value : 0)}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Mois</div>
              {monthList.length > 1 ? (
                <Select value={mois || ''} onValueChange={(v) => { setMois(v); setLoading(true); }}>
                  <SelectTrigger className="mt-0.5 h-8 w-[180px] capitalize" data-testid="rex-forecast-month-select">
                    <SelectValue placeholder="Choisir un mois" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthList.map((ym) => (
                      <SelectItem key={ym} value={ym} className="capitalize" data-testid={`rex-forecast-month-opt-${ym}`}>
                        {moisLabel(ym)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="font-medium capitalize" data-testid="rex-forecast-mois">{moisLabel(data.mois)} <Badge variant="outline" className="ml-1 text-[10px]">Forecast</Badge></div>
              )}
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Avancement</div>
              <div className="font-medium tabular-nums">Jour {data.prorata.jours_ecoules}/{data.prorata.jours_mois} · {data.prorata.pct}%</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Période de facturation</div>
              <div className="font-medium">{data.periode_facturation.start} → {data.periode_facturation.end}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Frais généraux (Qonto)</div>
              <div className="flex items-center gap-1.5" data-testid="rex-forecast-qonto-status">
                {data.qonto?.configured ? (
                  data.qonto?.error ? (
                    <Badge className="gap-1 bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"><AlertTriangle className="h-3 w-3" /> Erreur ({data.qonto.error})</Badge>
                  ) : (
                    <Badge className="gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Réel · {data.qonto.nb_txns || 0} txn</Badge>
                  )
                ) : (
                  <Badge className="gap-1 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"><CloudOff className="h-3 w-3" /> Non connecté</Badge>
                )}
              </div>
            </div>
            <Button variant="default" size="sm" onClick={syncQonto} disabled={syncingQonto}
              style={{ background: 'var(--tenant-accent-500)' }}
              data-testid="rex-forecast-qonto-sync">
              <RefreshCcwDot className={`mr-1.5 h-4 w-4 ${syncingQonto ? 'animate-spin' : ''}`} />
              {syncingQonto ? 'Synchronisation…' : 'Synchroniser Qonto'}
            </Button>
            <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="rex-forecast-refresh">
              <RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tableau P&L forecast */}
      <Card className="card-shadow">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="min-w-[220px]">Poste</TableHead>
                <TableHead className="text-right">Montant (à date)</TableHead>
                <TableHead className="w-[130px]">Nature</TableHead>
                <TableHead className="w-[150px] text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => {
                const src = SOURCE_CFG[r.source];
                const editable = ['ca', 'cout_rd', 'cout_re', 'cout_rp', 'cout_bureau', 'frais_generaux'].includes(r.key);
                const isCost = r.kind === 'cout';
                return (
                  <TableRow key={r.key} className={KIND_STYLE[r.kind] || ''} data-testid={`rex-forecast-row-${r.key}`}>
                    <TableCell className={r.kind === 'resultat' ? 'text-base' : ''}>{r.label}</TableCell>
                    <TableCell className={`text-right tabular-nums ${r.kind === 'resultat' ? 'text-base' : ''}`}
                      style={r.key === 'rex' ? { color: r.value >= 0 ? '#059669' : '#dc2626' } : undefined}
                      data-testid={`rex-forecast-val-${r.key}`}>
                      {isCost && r.value ? `− ${eur0(r.value)}` : eur0(r.value)}
                    </TableCell>
                    <TableCell>
                      {src ? (
                        <Badge className={src.cls} style={r.source === 'corrige' ? { background: 'var(--tenant-accent-500)' } : undefined}
                          data-testid={`rex-forecast-src-${r.key}`}>{src.label}</Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        {r.has_detail && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setDetailRow(r)} data-testid={`rex-forecast-detail-${r.key}`}>
                            <Info className="mr-1 h-3.5 w-3.5" />Détail
                          </Button>
                        )}
                        {editable && (
                          <Popover open={ovrRow === r.key} onOpenChange={(o) => { setOvrRow(o ? r.key : null); setOvrVal(o ? String(Math.round(r.value)) : ''); }}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Corriger ce poste" data-testid={`rex-forecast-edit-${r.key}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-64">
                              <div className="space-y-2">
                                <div className="text-xs font-medium">Corriger « {r.label} »</div>
                                <div className="flex items-center gap-2">
                                  <Input type="number" value={ovrVal} onChange={(e) => setOvrVal(e.target.value)}
                                    className="h-8" data-testid={`rex-forecast-ovr-input-${r.key}`} placeholder="Montant € HT" autoFocus />
                                  <Button size="sm" className="h-8" onClick={() => submitOverride(r.key)} data-testid={`rex-forecast-ovr-save-${r.key}`}>
                                    <Check className="h-4 w-4" />
                                  </Button>
                                </div>
                                {r.overridden && (
                                  <Button variant="ghost" size="sm" className="h-7 w-full justify-start text-xs text-muted-foreground" onClick={() => resetOverride(r.key)} data-testid={`rex-forecast-ovr-reset-${r.key}`}>
                                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Réinitialiser (calcul auto : {eur0(r.auto)})
                                  </Button>
                                )}
                                <p className="text-[11px] text-muted-foreground">Correction réversible, sans impact sur le réel.</p>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Détail INLINE (pas de dialog imbriqué : évite l'interception de pointeur) */}
      {detailRow && (
        <Card className="card-shadow border-2" style={{ borderColor: 'var(--tenant-accent-200)' }} data-testid="rex-forecast-detail-panel">
          <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Info className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />
                Détail du calcul — {detailRow.label}
              </CardTitle>
              <CardDescription>{moisLabel(data.mois)} · à date ({data.prorata.pct}% du mois)</CardDescription>
            </div>
            <Button variant="ghost" size="sm" className="h-8" onClick={() => setDetailRow(null)} data-testid="rex-forecast-detail-close">
              Fermer le détail
            </Button>
          </CardHeader>
          <CardContent>
            {detailRow.key === 'ca'
              ? <CaDetail detail={data.ca_detail} onRate={setRate} onResetRate={resetRate}
                  onAddRemb={addRemboursement} onDelRemb={delRemboursement} />
              : <CostDetail detail={data.costs_detail[detailRow.key]} label={detailRow.label}
                  regions={data.regions || []} onSetRegion={setRegion} />}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------- //
const RateCell = ({ value, onSave, onReset, testid }) => {
  const [v, setV] = useState(Math.round((value || 0) * 100));
  useEffect(() => { setV(Math.round((value || 0) * 100)); }, [value]);
  return (
    <div className="flex items-center justify-center gap-1">
      <Input type="number" value={v} onChange={(e) => setV(e.target.value)}
        className="h-7 w-14 px-1 text-center text-xs" data-testid={`rex-forecast-rate-input-${testid}`} />
      <span className="text-xs text-muted-foreground">%</span>
      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onSave(v)}
        data-testid={`rex-forecast-rate-save-${testid}`} title="Appliquer">
        <Check className="h-3.5 w-3.5" />
      </Button>
      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onReset}
        data-testid={`rex-forecast-rate-reset-${testid}`} title="Réinitialiser (auto)">
        <RotateCcw className="h-3 w-3" />
      </Button>
    </div>
  );
};

// --------------------------------------------------------------------------- //
const RemboursementsSection = ({ detail, onAdd, onDel }) => {
  const [asso, setAsso] = useState('');
  const [label, setLabel] = useState('');
  const [montant, setMontant] = useState('');
  const [prorata, setProrata] = useState(true);
  const rembs = detail.remboursements || [];
  const submit = () => {
    const m = Number(String(montant).replace(',', '.'));
    if (!m || isNaN(m)) { toast.error('Montant invalide'); return; }
    onAdd({ association: asso || null, label: label || 'Remboursement', montant_ht: m, prorata });
    setLabel(''); setMontant('');
  };
  return (
    <div data-testid="rex-forecast-remboursements">
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Remboursements manuels (déductions)</div>
      {rembs.length > 0 && (
        <Table className="mb-2 text-sm">
          <TableHeader><TableRow className="bg-muted/40">
            <TableHead>Association</TableHead><TableHead>Libellé</TableHead>
            <TableHead className="text-right">Montant HT</TableHead><TableHead className="text-right">Appliqué</TableHead><TableHead className="w-10" />
          </TableRow></TableHeader>
          <TableBody>
            {rembs.map((r) => (
              <TableRow key={r.id} data-testid={`rex-forecast-remb-${r.id}`}>
                <TableCell>{r.association || '—'}</TableCell>
                <TableCell>{r.label}</TableCell>
                <TableCell className="text-right tabular-nums">−{eur2(r.montant_ht)}</TableCell>
                <TableCell className="text-right tabular-nums text-rose-600">−{eur0(r.montant_applique)}{r.prorata ? ` (${pct(r.prorata_ratio)})` : ''}</TableCell>
                <TableCell><Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDel(r.id)} data-testid={`rex-forecast-remb-del-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/20 p-2">
        <Input placeholder="Asso (ex. AMNESTY)" value={asso} onChange={(e) => setAsso(e.target.value.toUpperCase())} className="h-8 w-40 text-xs" data-testid="rex-forecast-remb-asso" />
        <Input placeholder="Libellé (ex. régul. mois passés)" value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 flex-1 min-w-[160px] text-xs" data-testid="rex-forecast-remb-label" />
        <Input placeholder="Montant HT" value={montant} onChange={(e) => setMontant(e.target.value)} className="h-8 w-32 text-xs" data-testid="rex-forecast-remb-montant" />
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <input type="checkbox" checked={prorata} onChange={(e) => setProrata(e.target.checked)} data-testid="rex-forecast-remb-prorata" />
          proratiser
        </label>
        <Button size="sm" className="h-8" onClick={submit} data-testid="rex-forecast-remb-add">Ajouter</Button>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------- //
const CaDetail = ({ detail, onRate, onResetRate, onAddRemb, onDelRemb }) => {
  const warnings = detail.warnings || [];
  const sources = detail.sources || {};
  return (
    <div className="space-y-4">
      {/* Alertes qualité de données (tarif suspect, doublon de source) */}
      {warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200" data-testid={`rex-forecast-warning-${w.type}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{w.message}</span>
        </div>
      ))}

      {/* Rappel cohorte M-3 : montant d'attrition initiale attendu (anticipation) */}
      {detail.rappel_cohorte && detail.rappel_cohorte.total_attendu > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="rex-forecast-rappel-cohorte">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <b>Rappel cohorte {detail.rappel_cohorte.mois}</b> — attrition initiale attendue à déduire ce mois :
            <b> {eur0(detail.rappel_cohorte.total_attendu)}</b> (à date {eur0(detail.rappel_cohorte.a_date)},
            reste à venir {eur0(detail.rappel_cohorte.reste_a_venir)}).
            {detail.rappel_cohorte.par_association && detail.rappel_cohorte.par_association.length > 0 && (
              <> Dont {detail.rappel_cohorte.par_association.map((x) => `${x.association} ${eur0(x.attendu)}`).join(', ')}.</>
            )}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
        <CalendarRange className="h-4 w-4 text-muted-foreground" />
        Période de facturation : <b>{detail.periode.start} → {detail.periode.end}</b>
        <span className="text-muted-foreground">({detail.periode.nb_semaines} semaines, pilotées par le prélèvement du 8)</span>
        {typeof sources.evergiving === 'number' && (
          <span className="ml-auto text-xs text-muted-foreground" data-testid="rex-forecast-sources">
            Bulletins Evergiving comptés : <b className="text-foreground">{fmtInt(sources.evergiving)}</b>
            {sources.manuel_ignore ? ` · ${fmtInt(sources.manuel_ignore)} saisie(s) manuelle(s) ignorée(s) (doublon)` : ''}
          </span>
        )}
      </div>

      {/* Synthèse : brut → pré-attrition → attrition initiale → remboursements → net */}
      <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-5">
        <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">CA brut</div><div className="text-base font-semibold tabular-nums">{eur0(detail.total_brut)}</div></div>
        <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">− Pré-attrition</div><div className="text-base font-semibold tabular-nums text-rose-600">{eur0(detail.total_preattrition)}</div></div>
        <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">− Attrition initiale<br /><span className="text-[10px]">(cohorte {detail.cohorte_m3})</span></div><div className="text-base font-semibold tabular-nums text-rose-600">{eur0(detail.total_init_attrition)}</div></div>
        <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">− Remboursements</div><div className="text-base font-semibold tabular-nums text-rose-600">{eur0(detail.total_remboursements)}</div></div>
        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--tenant-accent-500)' }}><div className="text-xs text-muted-foreground">CA net (forecast)</div><div className="text-base font-bold tabular-nums" style={{ color: 'var(--tenant-accent-600)' }}>{eur0(detail.total_net)}</div></div>
      </div>

      {/* Par association : pré-attrition (mois courant) + attrition initiale (M-3) éditables */}
      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Par association — pré-attrition (mois courant) & attrition initiale (cohorte {detail.cohorte_m3}, proratisée {pct(detail.prorata)})
        </div>
        <div className="overflow-x-auto">
        <Table className="text-sm">
          <TableHeader><TableRow className="bg-muted/40">
            <TableHead>Association</TableHead><TableHead className="text-right">CA brut</TableHead>
            <TableHead className="w-[150px] text-center">Pré-attrition</TableHead>
            <TableHead className="w-[170px] text-center">Attrition initiale</TableHead>
            <TableHead className="text-right">CA net</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {detail.par_association.map((a) => (
              <TableRow key={a.association} data-testid={`rex-forecast-ca-assoc-${a.association}`}>
                <TableCell className="font-medium">{a.name}<div className="text-[10px] text-muted-foreground">{fmtInt(a.bulletins)} bull.</div></TableCell>
                <TableCell className="text-right tabular-nums">{eur0(a.ca_brut)}</TableCell>
                {/* Pré-attrition */}
                <TableCell>
                  <RateCell asso={a.association} value={a.preattrition_rate} testid={`preattr-${a.association}`}
                    onSave={(v) => onRate(`preattr:${a.association}`, v)} onReset={() => onResetRate(`preattr:${a.association}`)} />
                  <div className="mt-0.5 text-center text-[10px] text-muted-foreground">
                    −{eur0(a.preattrition)} · {a.preattrition_source === 'corrige' ? 'corrigé'
                      : a.preattrition_source === 'rendement_facturation' ? `auto (rdt ${Math.round((a.rendement || 0) * 100)}%)` : 'auto'}
                  </div>
                </TableCell>
                {/* Attrition initiale (cohorte M-3) */}
                <TableCell>
                  <RateCell asso={a.association} value={a.init_rate} testid={`initattr-${a.association}`}
                    onSave={(v) => onRate(`initattr:${a.association}`, v)} onReset={() => onResetRate(`initattr:${a.association}`)} />
                  <div className="mt-0.5 text-center text-[10px] text-muted-foreground">
                    −{eur0(a.init_attrition)} · base {eur0(a.init_base)} ({a.init_mois})
                    {a.init_source === 'corrige' ? ' · corrigé' : a.init_source === 'rapports_attrition' ? ' · auto (rapports)' : ''}
                  </div>
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">{eur0(a.ca_net)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Remboursements manuels */}
      <RemboursementsSection detail={detail} onAdd={onAddRemb} onDel={onDelRemb} />

      {/* Lignes canal × PU */}
      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Volumes × prix par canal</div>
        <div className="overflow-x-auto">
          <Table className="text-xs">
            <TableHeader><TableRow className="bg-muted/40">
              <TableHead>Association</TableHead><TableHead>Canal</TableHead>
              <TableHead className="text-right">Bulletins</TableHead><TableHead className="text-right">PU HT</TableHead>
              <TableHead className="text-right">CA brut</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {detail.lignes.map((l, i) => (
                <TableRow key={i} data-testid={`rex-forecast-ca-line-${i}`}>
                  <TableCell>{l.association}</TableCell>
                  <TableCell>{l.canal_terrain} <span className="text-muted-foreground">→ {l.canal_factu || '?'}</span></TableCell>
                  <TableCell className="text-right tabular-nums">{fmtInt(l.bulletins)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {l.prix_ht
                      ? (l.tarif_suspect
                        ? <span className="inline-flex items-center gap-1 font-medium text-rose-600" title="Tarif anormalement élevé — vérifiez la grille"><AlertTriangle className="h-3 w-3" />{eur2(l.prix_ht)}</span>
                        : eur2(l.prix_ht))
                      : <span className="text-rose-600">tarif manquant</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{eur0(l.ca_brut)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {detail.exceptions_tarif && detail.exceptions_tarif.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{detail.exceptions_tarif.length} ligne(s) sans tarif (non facturées) — renseignez le prix du canal dans la grille des tarifs.</span>
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------- //
const CostDetail = ({ detail, label, regions = [], onSetRegion }) => {
  if (!detail) return null;
  const baseLabel = detail.base_frozen ? 'mois clôturés' : 'derniers mois';
  const comp = detail.composantes;
  const regionsVent = detail.ventilation_regions || [];
  const postes = detail.postes || [];
  const collaborateurs = detail.collaborateurs || [];
  const indesBulletin = detail.indes_bulletin_detail || [];
  const indesQonto = detail.indes_qonto_detail || [];
  const indesQontoSrc = detail.indes_qonto_source;
  const nbSansRegion = detail.nb_sans_region || 0;
  const qonto = detail.qonto || null;
  const srcBadge = {
    reel_suivi_social: 'Réel Suivi Social', qonto_temps_reel: 'Réel Qonto (temps réel)',
    reel: 'Réel à date', moyenne_prorata: 'Moyenne × prorata', non_importe: 'Non encore importé',
  };
  return (
    <div className="space-y-3">
      <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
        <b>Méthode :</b> {detail.methode}
      </div>

      {/* Composantes du Coût RD (salaires + indé bulletin réel + indé Qonto) */}
      {comp && (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Composantes</div>
          <Table className="text-sm">
            <TableBody>
              {[['Salaires RD', comp.salaires_rd], ['Indé au bulletin (Suivi Social)', comp.inde_bulletin], ['Indé flux Qonto', comp.inde_qonto]].map(([lbl, c], i) => (
                <TableRow key={i}>
                  <TableCell className="text-muted-foreground">{lbl}
                    <Badge variant="outline" className="ml-2 text-[10px]">{srcBadge[c.source] || c.source}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{eur0(c.valeur)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Ventilation par région (Coût RD) */}
      {regionsVent.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Ventilation par région {detail.ventilation_source ? `(clé ${detail.ventilation_source})` : ''}</div>
          <Table className="text-sm">
            <TableBody>
              {regionsVent.map((r, i) => (
                <TableRow key={i}><TableCell className="text-muted-foreground">{r.region} <span className="text-[10px]">({pct(r.pct)})</span></TableCell><TableCell className="text-right tabular-nums">{eur0(r.cout)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Collaborateurs (salariés) & région — affectation MANUELLE 1-clic (override) */}
      {collaborateurs.length > 0 && (
        <div data-testid="rex-forecast-collaborateurs">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Users className="h-3.5 w-3.5" />Collaborateurs & région de recrutement
            {nbSansRegion > 0 && (
              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" data-testid="rex-forecast-region-todo">
                {nbSansRegion} à confirmer
              </Badge>
            )}
          </div>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Région déduite du <b>lieu de recrutement</b> (spots des bulletins). Corrigez en 1 clic si elle est erronée ou manquante — l'affectation manuelle prime sur l'automatique.
          </p>
          <div className="overflow-x-auto">
            <Table className="text-sm">
              <TableHeader><TableRow className="bg-muted/40">
                <TableHead>Collaborateur</TableHead><TableHead className="w-14 text-center">Bloc</TableHead>
                <TableHead className="text-right">Coût est.</TableHead>
                <TableHead className="w-[230px]">Région (affectation)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {collaborateurs.map((c) => (
                  <TableRow key={c.matricule}
                    className={c.needs_confirmation ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}
                    data-testid={`rex-forecast-collab-${c.matricule}`}>
                    <TableCell>
                      <div className="font-medium">{c.nom}</div>
                      <div className="text-[10px] text-muted-foreground">{c.matricule}</div>
                    </TableCell>
                    <TableCell className="text-center"><Badge variant="outline" className="text-[10px]">{c.bloc}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{eur0(c.cout)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <MapPin className={`h-3.5 w-3.5 shrink-0 ${c.needs_confirmation ? 'text-amber-500' : 'text-muted-foreground'}`} />
                        <Select value={c.needs_confirmation ? undefined : c.region}
                          onValueChange={(v) => onSetRegion(c.matricule, v)}>
                          <SelectTrigger className="h-8 w-[190px] text-xs" data-testid={`rex-forecast-region-select-${c.matricule}`}>
                            <SelectValue placeholder="À affecter…" />
                          </SelectTrigger>
                          <SelectContent>
                            {regions.map((rg) => (
                              <SelectItem key={rg} value={rg} className="text-xs" data-testid={`rex-forecast-region-opt-${c.matricule}-${rg}`}>{rg}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {c.source === 'override' && (
                          <Badge className="text-[9px]" style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} title={`Auto: ${c.region_auto || 'aucune'}`}>manuel</Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Indépendants au bulletin — RÉEL À DATE (paie), pas le forecast plat */}
      {indesBulletin.length > 0 && (
        <div data-testid="rex-forecast-indes-bulletin">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Indépendants au bulletin <Badge variant="outline" className="text-[10px]">réel à date</Badge>
          </div>
          <Table className="text-sm">
            <TableBody>
              {indesBulletin.map((p, i) => (
                <TableRow key={p.matricule || i} data-testid={`rex-forecast-inde-${p.matricule || i}`}>
                  <TableCell className="text-muted-foreground">{p.nom} <span className="text-[10px]">({p.matricule})</span></TableCell>
                  <TableCell className="text-right tabular-nums">{eur0(p.montant)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {typeof detail.indes_bulletin_projection === 'number' && detail.indes_bulletin_projection > 0 && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Projection fin de mois (réel à date extrapolé) : <b>{eur0(detail.indes_bulletin_projection)}</b>.
            </p>
          )}
        </div>
      )}

      {/* Indépendants flux Qonto — réel si virement passé, sinon estimation moyenne × prorata */}
      {indesQonto.length > 0 && (
        <div data-testid="rex-forecast-indes-qonto">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Indépendants (flux Qonto)
            <Badge variant="outline" className="text-[10px]">
              {indesQontoSrc === 'qonto_temps_reel' ? 'réel Qonto' : 'estimé (moyenne mois précédents × prorata)'}
            </Badge>
          </div>
          <Table className="text-sm">
            <TableBody>
              {indesQonto.map((p, i) => (
                <TableRow key={i} data-testid={`rex-forecast-indeqonto-${i}`}>
                  <TableCell className="text-muted-foreground">{p.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur0(p.montant)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {indesQontoSrc !== 'qonto_temps_reel' && (
            <p className="mt-1 text-[11px] text-muted-foreground">Virement non encore passé ce mois → estimation par prestataire (moyenne des mois précédents × prorata).</p>
          )}
        </div>
      )}

      {/* Frais généraux / Bureau / Indés flux Qonto : ventilation Qonto poste par poste (RÉEL à date) */}
      {qonto && (
        <div data-testid="rex-forecast-qonto-detail">
          {!qonto.configured ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
              <CloudOff className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Qonto non connecté : aucune estimation n'est faite. Déposez la clé API (lecture seule) dans <b>Suivi Social → Qonto</b>, puis cliquez <b>« Synchroniser Qonto »</b> pour récupérer le réel du mois.</span>
            </div>
          ) : qonto.error ? (
            <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50/60 px-3 py-2 text-[12px] text-rose-800 dark:border-rose-900 dark:bg-rose-950/20 dark:text-rose-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Récupération Qonto échouée ({qonto.error}). Relancez la synchronisation.</span>
            </div>
          ) : (
            <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {detail.postes_reel === false ? 'Ventilation estimée (moyenne mois précédents × prorata)' : 'Ventilation Qonto (réel à date)'}
              <Badge className={detail.postes_reel === false
                ? 'bg-amber-100 text-[10px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                : 'bg-emerald-100 text-[10px] text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'}>
                {detail.postes_reel === false ? 'estimé (virement non passé)' : `${qonto.nb_txns || 0} transaction(s)`}
              </Badge>
            </div>
          )}
          {postes.length > 0 && (
            <Table className="text-sm">
              <TableBody>
                {postes.map((p, i) => (
                  <TableRow key={i} data-testid={p.estime ? 'rex-forecast-fg-poste-tr-estime' : `rex-forecast-fg-poste-${i}`}>
                    <TableCell className="text-muted-foreground">
                      {p.label} <span className="text-[10px]">({p.nb})</span>
                      {p.estime && (
                        <Badge
                          className="ml-2 bg-amber-100 text-[10px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                          title={`Virement Swile non encore passé ce mois → estimation depuis le Suivi Social : ${p.nb} TR × 8 € ÷ 1,5`}
                          data-testid="rex-forecast-fg-tr-badge">
                          Estimé (Nb TR × 8€ ÷ 1,5)
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{eur0(p.montant)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {qonto.configured && !qonto.error && postes.length === 0 && (
            <p className="mt-1 text-[11px] text-muted-foreground">Aucune transaction sur ce poste ce mois pour l'instant (réel à date = 0 €).</p>
          )}
        </div>
      )}

      {/* Ventilation poste par poste (lignes non-Qonto le cas échéant) */}
      {!qonto && postes.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Ventilation poste par poste</div>
          <Table className="text-sm">
            <TableBody>
              {postes.map((p, i) => (
                <TableRow key={i}><TableCell className="text-muted-foreground">{p.label} <span className="text-[10px]">({p.nb})</span></TableCell><TableCell className="text-right tabular-nums">{eur0(p.montant)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Base d'estimation — masquée pour les lignes basées sur le réel Qonto */}
      {!qonto && (
      <Table className="text-sm">
        <TableBody>
          <TableRow><TableCell className="text-muted-foreground">{`Moyenne (${detail.nb_mois_moyenne} ${baseLabel}${detail.base_mois && detail.base_mois.length ? ' : ' + detail.base_mois.join(', ') : ''})`}</TableCell><TableCell className="text-right tabular-nums">{eur0(detail.moyenne_mois_precedents)}</TableCell></TableRow>
          <TableRow><TableCell className="text-muted-foreground">Prorata du mois appliqué</TableCell><TableCell className="text-right tabular-nums">{pct(detail.prorata)}</TableCell></TableRow>
          <TableRow><TableCell className="text-muted-foreground">Estimation (moyenne × prorata)</TableCell><TableCell className="text-right tabular-nums">{eur0(detail.estimation)}</TableCell></TableRow>
          {typeof detail.structure_suivi_social === 'number' && detail.structure_suivi_social > 0 && (
            <TableRow><TableCell className="text-muted-foreground">Structure (Suivi Social, info)</TableCell><TableCell className="text-right tabular-nums">{eur0(detail.structure_suivi_social)}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      )}

      {detail.suivi_social && (
        <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200" data-testid="rex-forecast-cost-tr">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{detail.suivi_social.note}</span>
        </div>
      )}
    </div>
  );
};

export default ForecastView;
